import { Link } from "wouter";
import { Calendar, MapPin, ArrowRight, Trophy, Briefcase } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { User as UserType, Event } from "@shared/schema";

interface EventsTabProps {
  events: Event[];
  leaders: UserType[];
  eventsLoading: boolean;
  leadersLoading: boolean;
}

function EventCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <Skeleton className="h-14 w-14 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LeaderCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center text-center">
          <Skeleton className="h-20 w-20 rounded-full mb-3" />
          <Skeleton className="h-5 w-28 mb-1" />
          <Skeleton className="h-4 w-32 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return {
    month: date.toLocaleDateString("en-US", { month: "short" }),
    day: date.getDate(),
  };
}

function getEventTypeBadgeVariant(type: string): "default" | "secondary" | "outline" {
  switch (type) {
    case "summit": return "default";
    case "webinar": return "secondary";
    default: return "outline";
  }
}

function getInitials(name?: string | null) {
  if (!name) return "U";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

export function EventsTab({ events, leaders, eventsLoading, leadersLoading }: EventsTabProps) {
  const displayEvents = events || [];
  const displayLeaders = leaders || [];

  return (
    <div className="space-y-8">
      <section data-testid="section-events-list">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">Upcoming Events</h2>
            <p className="text-muted-foreground">Don't miss these upcoming opportunities</p>
          </div>
          <Button variant="outline" asChild data-testid="button-view-all-events">
            <Link href="/events">
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {eventsLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => <EventCardSkeleton key={i} />)}
          </div>
        ) : displayEvents.length === 0 ? (
          <Card data-testid="empty-events">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No upcoming events</p>
                <p className="text-sm text-muted-foreground mt-1">Check back soon for new events and opportunities.</p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link href="/events" data-testid="link-browse-events">Browse Events</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayEvents.slice(0, 6).map((event: any) => {
              const dateInfo = formatDate(event.startDate);
              return (
                <Card key={event.id} className="hover-elevate" data-testid={`card-event-${event.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-14 h-14 bg-primary/10 rounded-md flex flex-col items-center justify-center">
                        <span className="text-xs font-medium text-primary uppercase">{dateInfo.month}</span>
                        <span className="text-lg font-bold text-primary">{dateInfo.day}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge
                            variant={getEventTypeBadgeVariant(event.type)}
                            className="no-default-active-elevate capitalize"
                            data-testid={`badge-event-type-${event.id}`}
                          >
                            {event.type}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-foreground mb-1 line-clamp-2" data-testid={`text-event-title-${event.id}`}>
                          {event.title}
                        </h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1" data-testid={`text-event-location-${event.id}`}>
                          <MapPin className="h-3 w-3" />
                          {event.location}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button size="sm" data-testid={`button-register-${event.id}`}>Register</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section data-testid="section-events-leaders">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">100 Leaders Spotlight</h2>
            <p className="text-muted-foreground">Meet the most influential leaders in tech</p>
          </div>
          <Button variant="outline" asChild data-testid="button-view-all-leaders">
            <Link href="/leaders">
              View Full List
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {leadersLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => <LeaderCardSkeleton key={i} />)}
          </div>
        ) : displayLeaders.length === 0 ? (
          <Card data-testid="empty-leaders">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No featured leaders yet</p>
                <p className="text-sm text-muted-foreground mt-1">Check back soon for the latest leaders spotlight.</p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link href="/leaders" data-testid="link-browse-leaders">View Leaders</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayLeaders.slice(0, 4).map((leader: any) => (
              <Link key={leader.id} href={`/leaders/${leader.id}`}>
                <Card className="h-full hover-elevate cursor-pointer" data-testid={`card-leader-${leader.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative mb-3">
                        <Avatar className="h-20 w-20" data-testid={`avatar-leader-${leader.id}`}>
                          <AvatarImage src={leader.imageUrl || leader.profileImageUrl || undefined} alt={leader.name || `${leader.firstName} ${leader.lastName}`} />
                          <AvatarFallback className="text-lg">
                            {getInitials(leader.name || `${leader.firstName || ""} ${leader.lastName || ""}`)}
                          </AvatarFallback>
                        </Avatar>
                        {leader.rank && (
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-secondary rounded-full flex items-center justify-center" data-testid={`badge-rank-${leader.id}`}>
                            <span className="text-xs font-bold text-secondary-foreground">#{leader.rank}</span>
                          </div>
                        )}
                      </div>
                      <h3 className="font-semibold text-foreground mb-1" data-testid={`text-leader-name-${leader.id}`}>
                        {leader.name || `${leader.firstName || ""} ${leader.lastName || ""}`}
                      </h3>
                      <p className="text-sm text-muted-foreground">{leader.title}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {leader.company}
                      </p>
                      {leader.year && (
                        <Badge variant="outline" className="mt-2 no-default-active-elevate" data-testid={`badge-year-${leader.id}`}>
                          <Trophy className="h-3 w-3 mr-1" />
                          {leader.year} List
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
